import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../home/components/Footer';
import PageCTA from '../../components/feature/PageCTA';

export default function Tier3CompetitiveIntelligence() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-600 via-cyan-600 to-blue-600 text-white py-32 overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full mb-6">
              <i className="ri-line-chart-line text-2xl"></i>
              <span className="font-semibold text-lg">$899</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Tier 3 — Competitive Intelligence™
            </h1>
            <p className="text-xl md:text-2xl text-teal-50 mb-10 leading-relaxed">
              Understand exactly where your app stands in the competitive landscape — strengths, weaknesses, positioning, and threats.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/submit-app"
                className="inline-block bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-8 py-4 rounded-xl font-semibold text-base hover:from-emerald-700 hover:to-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
              >
                Get Competitive Intelligence
              </Link>
              <Link
                to="/sample-report"
                className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer"
              >
                Download Sample Report
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Who This Is For
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              'Apps already launched',
              'Apps not getting traction',
              'Founders unsure why growth is slow',
              'Apps in competitive categories',
              'Founders preparing to scale',
              'Anyone who wants a data-driven view of their competition'
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all">
                <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-500 rounded-lg flex-shrink-0">
                  <i className="ri-check-line text-white text-xl"></i>
                </div>
                <p className="text-gray-700 font-medium">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What You Get */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What You Get
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A Deep Competitive Breakdown That Shows Your Real Position
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-bar-chart-box-line',
                title: 'Category Standards Benchmark',
                description: 'Measures how your app compares to the top-performing apps in your category.'
              },
              {
                icon: 'ri-git-compare-line',
                title: 'Competitive Gap Analysis',
                description: 'Identifies where you\'re ahead, behind, or missing critical success indicators.'
              },
              {
                icon: 'ri-function-line',
                title: 'Feature Strength Index',
                description: 'Evaluates whether your features help or hurt your market position.'
              },
              {
                icon: 'ri-emotion-line',
                title: 'Emotional Response Snapshot',
                description: 'Predicts how users will feel when interacting with your app (value, clarity, trust, motivation, frustration).'
              },
              {
                icon: 'ri-route-line',
                title: 'Opportunity Route',
                description: 'Shows the smartest strategic angle to outperform competitors.'
              },
              {
                icon: 'ri-lightbulb-flash-line',
                title: '1 High-Impact Recommendation',
                description: 'A precise next move based on your strongest opportunity.'
              }
            ].map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-teal-50 to-cyan-50 p-8 rounded-2xl border border-teal-100 hover:shadow-lg transition-all">
                <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl mb-6">
                  <i className={`${feature.icon} text-white text-2xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">
            Why It Matters
          </h2>
          <p className="text-xl text-gray-300 leading-relaxed mb-6">
            Founders often compare themselves to competitors incorrectly.
          </p>
          <p className="text-xl text-gray-300 leading-relaxed mb-6">
            Tier 3 reveals exactly where you stand — backed by structured patterns and top-company benchmarks.
          </p>
          <p className="text-xl text-teal-400 font-semibold leading-relaxed">
            You walk away knowing your strengths, weaknesses, and how to win in your category.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <PageCTA 
        title="Get Your Competitive Intelligence Report"
        description="Understand exactly where you stand in your competitive landscape."
        primaryText="Get Competitive Intelligence"
        primaryLink="/submit-app"
        bgGradient="from-teal-600 via-cyan-600 to-blue-600"
      />

      <Footer />
    </div>
  );
}
